import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import './ModelViewer.css';
import { getCardioInfo } from './cardioData';
import { useFocusMeshOnLoad } from './useFocusMeshOnLoad';
import { useMeshSelection } from './useMeshSelection';
import ModelSearchBar from './ModelSearchBar';

const NEON_RED = new THREE.Color(0xff1744);
const NEON_RED_BRIGHT = new THREE.Color(0xff5252);
const NEON_BLUE = new THREE.Color(0x2979ff);
const NEON_BLUE_BRIGHT = new THREE.Color(0x448aff);

function isVeinName(name) {
  const n = (name || '').toLowerCase();
  return n.includes('vein') || n.includes('vena') || n.includes('cava');
}

function CardiovascularFull() {
  const navigate = useNavigate();
  const modelRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);

  const originalMaterialsRef = useRef(new Map());
  const highlightedMeshRef = useRef(null);
  const highlightRafRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const pumpRafRef = useRef(null);
  const heartMeshesRef = useRef([]);

  const forceRender = useCallback(() => {
    const mv = modelRef.current;
    if (!mv) return;
    if (mv.requestUpdate) mv.requestUpdate();
    try {
      const expAttr = mv.getAttribute('exposure') || '1';
      const exp = parseFloat(expAttr);
      mv.setAttribute('exposure', (exp + 0.000001).toString());
      requestAnimationFrame(() => {
        if (modelRef.current) modelRef.current.setAttribute('exposure', expAttr);
      });
    } catch (e) { }
  }, []);

  const getInternalScene = useCallback(() => {
    if (sceneRef.current && cameraRef.current) return { scene: sceneRef.current, camera: cameraRef.current };
    const mv = modelRef.current;
    if (!mv) return null;

    let scene = null, camera = null;
    const symbols = Object.getOwnPropertySymbols(mv);
    for (const sym of symbols) {
      const val = mv[sym];
      if (val && val.isScene) scene = val;
      if (val && (val.isCamera || val.isPerspectiveCamera)) camera = val;
      if (val && val.scene && val.scene.isScene) scene = val.scene;
      if (val && val.camera && (val.camera.isCamera || val.camera.isPerspectiveCamera)) camera = val.camera;
    }
    if (!scene) {
      const keys = Object.getOwnPropertyNames(mv);
      for (const key of keys) {
        try {
          const val = mv[key];
          if (val && val.isScene) scene = val;
          if (val && (val.isCamera || val.isPerspectiveCamera)) camera = val;
        } catch (e) {}
      }
    }
    if (scene) {
      sceneRef.current = scene;
      cameraRef.current = camera || mv.camera;
      return { scene: sceneRef.current, camera: cameraRef.current };
    }
    return null;
  }, []);

  const findHeartMeshes = useCallback(() => {
    if (heartMeshesRef.current.length > 0) return;
    const ctx = getInternalScene();
    if (!ctx) return;
    const keywords = ['cardiac', 'heart', 'back_heart', 'capslice', 'pulmonary_vein'];
    ctx.scene.traverse((obj) => {
      if (!obj.isMesh) return;
      const name = (obj.name || '').toLowerCase().replace(/\s/g, '');
      const isHeart = keywords.some(kw => name.includes(kw));
      if (isHeart) {
        heartMeshesRef.current.push({ mesh: obj, originalScale: obj.scale.clone() });
      }
    });
  }, [getInternalScene]);

  const highlightMesh = useCallback((mesh) => {
    if (!mesh || !mesh.material) return;

    if (!originalMaterialsRef.current.has(mesh.uuid)) {
      const orig = mesh.material;
      let cloned;
      if (Array.isArray(orig)) {
        cloned = orig.map(m => (m && typeof m.clone === 'function') ? m.clone() : null).filter(Boolean);
      } else if (orig && typeof orig.clone === 'function') {
        cloned = orig.clone();
      }
      if (!cloned) return;
      mesh.material = cloned;

      const mats = Array.isArray(cloned) ? cloned : [cloned];
      const props = mats.map(m => ({
        color: (m.color && typeof m.color.clone === 'function') ? m.color.clone() : new THREE.Color(0xffffff),
        opacity: m.opacity ?? 1,
        transparent: !!m.transparent,
        emissive: (m.emissive && typeof m.emissive.clone === 'function') ? m.emissive.clone() : new THREE.Color(0x000000),
        emissiveIntensity: m.emissiveIntensity ?? 1
      }));
      originalMaterialsRef.current.set(mesh.uuid, props);
    }

    highlightedMeshRef.current = mesh;
    const meshName = mesh.name || '';

    if (highlightRafRef.current) cancelAnimationFrame(highlightRafRef.current);

    const neonColor = isVeinName(meshName) ? NEON_BLUE : NEON_RED;
    const neonBright = isVeinName(meshName) ? NEON_BLUE_BRIGHT : NEON_RED_BRIGHT;
    const darkBase = isVeinName(meshName) ? new THREE.Color(0x001133) : new THREE.Color(0x330011);

    const pulseLoop = (time) => {
      const m = highlightedMeshRef.current;
      if (!m) return;

      const pulse = 0.5 + Math.sin(time * 0.004) * 0.5;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      mats.forEach(mat => {
        if (mat.color) mat.color.copy(neonColor).lerp(darkBase, 1 - pulse);
        if (mat.emissive) {
          mat.emissive.copy(neonBright);
          mat.emissiveIntensity = 2.0 + pulse * 4.0;
        }
        mat.transparent = true;
        mat.opacity = 0.7 + pulse * 0.3;
      });
      forceRender();
      highlightRafRef.current = requestAnimationFrame(pulseLoop);
    };
    highlightRafRef.current = requestAnimationFrame(pulseLoop);
  }, [forceRender]);

  const resetHighlight = useCallback(() => {
    if (highlightRafRef.current) {
      cancelAnimationFrame(highlightRafRef.current);
      highlightRafRef.current = null;
    }
    const high = highlightedMeshRef.current;
    if (!high || !high.isMesh) return;

    const props = originalMaterialsRef.current.get(high.uuid);
    if (!props) return;

    const mats = Array.isArray(high.material) ? high.material : [high.material];
    mats.forEach((m, i) => {
      const p = props[i];
      if (m && p) {
        if (m.color && p.color) m.color.set(p.color);
        m.opacity = p.opacity;
        m.transparent = p.transparent;
        if (m.emissive && p.emissive) m.emissive.set(p.emissive);
        if (p.emissiveIntensity !== undefined) m.emissiveIntensity = p.emissiveIntensity;
      }
    });
    highlightedMeshRef.current = null;
    forceRender();
  }, [forceRender]);

  const { selectedPart, hotspotPosition, screenSide, hotspotKey, applySelection, closeLabel } =
    useMeshSelection({ infoLookup: getCardioInfo, highlightMesh, resetHighlight });

  const pumpLoop = useCallback((time) => {
    if (heartMeshesRef.current.length === 0) {
      findHeartMeshes();
      pumpRafRef.current = requestAnimationFrame(pumpLoop);
      return;
    }

    const bpm = 72;
    const cycleDuration = (60 / bpm) * 1000;
    const t = (time % cycleDuration) / cycleDuration;

    const beat = (phase, amp) => Math.sin(phase * Math.PI) * amp;
    let sx = 1, sy = 1, sz = 1;

    if (t < 0.07) {
      const p = t / 0.07;
      sy = 1 - beat(p, 0.04);
      sx = 1 + beat(p, 0.02);
      sz = 1 + beat(p, 0.02);
    } else if (t < 0.12) {
      const p = (t - 0.07) / 0.05;
      sy = 1 + beat(p, 0.008);
      sx = 1 - beat(p, 0.004);
      sz = 1 - beat(p, 0.004);
    } else if (t < 0.25) {
      const p = (t - 0.12) / 0.13;
      sy = 1 + beat(p, 0.05);
      sx = 1 - beat(p, 0.03);
      sz = 1 - beat(p, 0.025);
    } else if (t < 0.35) {
      const p = (t - 0.25) / 0.10;
      sx = 1 + beat(p, 0.01);
      sy = 1 - beat(p, 0.01);
      sz = 1 + beat(p, 0.01);
    } else {
      const p = (t - 0.35) / 0.65;
      const drift = Math.sin(p * Math.PI * 2) * 0.002;
      sx = 1 + drift;
      sy = 1 - drift;
      sz = 1 + drift;
    }

    heartMeshesRef.current.forEach(({ mesh, originalScale }) => {
      mesh.scale.set(originalScale.x * sx, originalScale.y * sy, originalScale.z * sz);
    });

    forceRender();
    pumpRafRef.current = requestAnimationFrame(pumpLoop);
  }, [forceRender, findHeartMeshes]);

  const onFound = useCallback((mesh, center, normal) => {
    applySelection(mesh, center, normal);
  }, [applySelection]);

  const { focusMeshIfPending, focusMesh } = useFocusMeshOnLoad({ modelRef, onFound });

  useEffect(() => {
    const mv = modelRef.current;
    if (!mv) return;
    const handleLoad = () => {
      setIsLoading(false);
      focusMeshIfPending();
      setTimeout(() => {
        findHeartMeshes();
        pumpRafRef.current = requestAnimationFrame(pumpLoop);
      }, 300);
    };
    mv.addEventListener('load', handleLoad);
    if (mv.loaded) handleLoad();
    return () => mv.removeEventListener('load', handleLoad);
  }, [findHeartMeshes, pumpLoop, focusMeshIfPending]);

  const handleModelClick = useCallback((event) => {
    const mv = modelRef.current;
    if (!mv) return;

    const rect = mv.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const hit = mv.positionAndNormalFromPoint(x, y);
    if (!hit) {
      closeLabel();
      return;
    }

    const ctx = getInternalScene();
    let identifiedMesh = null;
    let identifiedName = null;

    if (ctx && ctx.camera) {
      const ndcX = (x / rect.width) * 2 - 1;
      const ndcY = -(y / rect.height) * 2 + 1;
      raycasterRef.current.setFromCamera(new THREE.Vector2(ndcX, ndcY), ctx.camera);
      const intersects = raycasterRef.current.intersectObjects(ctx.scene.children, true);
      const firstMesh = intersects.find(i => {
        const obj = i.object;
        if (!obj.isMesh) return false;
        const name = (obj.name || '').toLowerCase();
        const matName = (obj.material?.name || '').toLowerCase();
        if (name.includes('shader') || name.includes('shadow') || name.includes('ground') || name.includes('helper')) return false;
        if (matName.includes('shader') || matName.includes('shadow') || matName.includes('ground')) return false;
        return true;
      })?.object;
      if (firstMesh) {
        identifiedMesh = firstMesh;
        identifiedName = firstMesh.name || firstMesh.material?.name;
      }
    }

    if (!identifiedName) {
      const mvMat = mv.materialFromPoint(x, y);
      if (mvMat && mvMat.name) identifiedName = mvMat.name;
    }

    if (identifiedName && identifiedName !== 'Unknown') {
      applySelection(identifiedMesh, hit.position, hit.normal);
    } else {
      closeLabel();
    }
  }, [getInternalScene, applySelection, closeLabel]);

  useEffect(() => {
    return () => {
      if (pumpRafRef.current) cancelAnimationFrame(pumpRafRef.current);
      if (highlightRafRef.current) cancelAnimationFrame(highlightRafRef.current);
    };
  }, []);

  return (
    <div className="model-viewer-page skeleton-page skeleton-surface-click">
      <button onClick={() => navigate('/menu')} className="back-btn">← Back to Menu</button>

      <ModelSearchBar currentSystemId="cardiovascularsystem" onFocusMesh={focusMesh} />

      {isLoading && (
        <div className="skeleton-mv-loading">
          <div className="skeleton-loading-spinner"></div>
          <p>Loading Cardiovascular System...</p>
        </div>
      )}

      <model-viewer
        ref={modelRef}
        src="/models/Cardiovascular.glb"
        alt="3D Cardiovascular System"
        ar camera-controls touch-action="none" shadow-intensity="1"
        camera-orbit="0deg 75deg 2m" field-of-view="45deg"
        min-camera-orbit="auto auto 0.1m" max-camera-orbit="auto auto 4m"
        onClick={handleModelClick}
        style={{ width: '100%', height: '100%', backgroundColor: '#ffffff', cursor: 'var(--cursor-pointer)', '--progress-bar-color': '#00E5FF', '--progress-bar-height': '4px' }}
      >
        {selectedPart && hotspotPosition && (
          <div
            key={hotspotKey}
            className={`skeleton-leader-hotspot ${screenSide}`}
            slot="hotspot-part"
            data-position={hotspotPosition.position}
            data-normal={hotspotPosition.normal}
          >
            <div className="skeleton-leader-dot"></div>
            <div className="skeleton-leader-line"></div>
            <div className="skeleton-leader-label">
              <div className="skeleton-leader-content">
                <span className="skeleton-leader-english">{selectedPart.english}</span>
                <span className="skeleton-leader-latin">{selectedPart.latin}</span>
              </div>
              <button className="skeleton-leader-close" onClick={closeLabel}>✕</button>
            </div>
          </div>
        )}
      </model-viewer>
    </div>
  );
}

function CardiovascularSystem() {
  
  return <CardiovascularFull />;
}

export default CardiovascularSystem;
